import { useEffect, useRef, useState } from "react";
import { SectionHeading, Reveal } from "@/components/Section";
import { EXPLODE_VIDEO, CAD_OVERLAY, ASSETS } from "@/lib/assets";

const CLEAN_SRC = "/models/SurfBand-Flat-Cap-Clean.glb";
const LABELED_SRC = "/models/SurfBand-Flat-Cap-Labeled.glb";

const VIEWS = {
  overview: ["155deg 65deg 0.13m", "0m -0.023m 0m"],
  push: ["180deg 40deg 0.12m", "0m 0.008m 0m"],
  valve: ["180deg 78deg 0.10m", "0m 0.006m -0.018m"],
  cap: ["90deg 130deg 0.125m", "0m -0.0033m 0m"],
};

const FEATURES = [
  { key: "overview", label: "Overview" },
  { key: "push", label: "Push to Dispense" },
  { key: "valve", label: "Valve" },
  { key: "cap", label: "Refillable Cap" },
  { key: "exploded", label: "Exploded View" },
];

const MODES = [
  { key: "callouts", label: "Callouts" },
  { key: "labels", label: "Labels on Model" },
  { key: "clean", label: "Band Only" },
];

// The clean and labeled GLBs share one geometry; color variants are achieved by
// retinting the "Translucent magenta LDPE" material (reservoir + cap) at runtime
// via model-viewer's material API, since separate per-color GLBs are not available.
const ACCENT_MATERIAL = "Translucent magenta LDPE - photo approximation";
const COLORS = [
  { key: "coral", label: "Coral", swatch: "#f1032a", rgba: [0.9489649381781952, 0.009021491898012131, 0.16044351072534352, 1] },
  { key: "ocean", label: "Ocean Blue", swatch: "#6f93d6", rgba: [0.42, 0.56, 0.82, 1] },
  { key: "charcoal", label: "Charcoal", swatch: "#3a3a3d", rgba: [0.1, 0.105, 0.115, 1] },
];

export default function BandExplorer({ variant = "home" }) {
  const bandRef = useRef(null);
  const [feature, setFeature] = useState("overview");
  const [mode, setMode] = useState("callouts");
  const [color, setColor] = useState("coral");
  const [ready, setReady] = useState(false);
  const colorRef = useRef(color);
  const featureRef = useRef(feature);
  colorRef.current = color;
  featureRef.current = feature;

  const showFeature = (name) => {
    setFeature(name);
    if (name === "exploded") return;
    const band = bandRef.current;
    if (!band) return;
    const [orbit, target] = VIEWS[name];
    band.fieldOfView = "45deg";
    band.cameraOrbit = orbit;
    band.cameraTarget = target;
    requestAnimationFrame(() => requestAnimationFrame(() => band.jumpCameraToGoal && band.jumpCameraToGoal()));
  };

  const applyColor = (key) => {
    const band = bandRef.current;
    if (!band || !band.model) return;
    const picked = COLORS.find((c) => c.key === key) || COLORS[0];
    const material = band.model.materials?.find((m) => m.name === ACCENT_MATERIAL);
    material?.pbrMetallicRoughness?.setBaseColorFactor(picked.rgba);
  };

  useEffect(() => {
    const band = bandRef.current;
    if (!band) return;
    const onLoad = () => {
      setReady(true);
      showFeature(featureRef.current);
      applyColor(colorRef.current);
    };
    band.addEventListener("load", onLoad);
    return () => band.removeEventListener("load", onLoad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMode = (m) => {
    setMode(m);
    const band = bandRef.current;
    if (!band) return;
    const nextSrc = m === "labels" ? LABELED_SRC : CLEAN_SRC;
    if (band.getAttribute("src") !== nextSrc) band.setAttribute("src", nextSrc);
  };

  const handleColor = (key) => {
    setColor(key);
    applyColor(key);
  };

  const showHotspots = mode === "callouts";

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28" data-testid="band-explorer-section">
      <SectionHeading
        overline="Explore in 3D"
        title={
          variant === "platform"
            ? "Turn it over. Look inside."
            : variant === "technology"
            ? "The mechanism, in your hands."
            : "This is the actual product."
        }
        subtitle={
          variant === "platform"
            ? "The same patented hardware from every angle. Rotate it, zoom in, and open the exploded view to see the valve and reservoir architecture that earned the patent."
            : variant === "technology"
            ? "Rotate the real reservoir and breathing valve shown above, or open the exploded view for the architecture underneath."
            : "Shown here in our Velcro Sports Edition, built for water sports and active days at the beach. We've also manufactured a silicone band for easier everyday wear."
        }
        testId="band-explorer-heading"
      />

      {variant === "home" && (
        <Reveal delay={0.05}>
          <figure className="mt-10 rounded-2xl overflow-hidden border border-zinc-800" data-testid="band-explorer-real-photo">
            <img
              src={ASSETS.translucentGlow}
              alt="Unedited photo of the Safe Band Pro reservoir glowing magenta as sunlight passes through its translucent material"
              className="w-full h-auto object-cover"
            />
            <figcaption className="font-mono text-[10px] tracking-[0.14em] uppercase text-zinc-500 leading-relaxed px-5 sm:px-7 py-4 bg-[#0a0a0a]">
              Unretouched. No render, no filter. That magenta glow is ordinary sunlight passing
              straight through the translucent reservoir on an actual, manufactured Safe Band Pro.
            </figcaption>
          </figure>
        </Reveal>
      )}

      {variant === "home" && (
        <Reveal delay={0.08}>
          <div className="mt-14 text-center max-w-2xl mx-auto" data-testid="band-explorer-cad-intro">
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-4">
              CAD Model &middot; Interactive 3D
            </span>
            <h3 className="font-heading font-light tracking-tight leading-[1.1] text-2xl sm:text-3xl text-white">
              Here's the CAD product, in rotational form.
            </h3>
            <p className="font-body text-zinc-300 text-base leading-relaxed mt-4">
              Rotate it, zoom in, and tap a feature to see how it works: the push-to-dispense
              top, the breathing valve, and the refillable cap.
            </p>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.1}>
        <div className={`${variant === "home" ? "mt-10" : "mt-14"} border border-zinc-800 bg-[#0a0a0a] rounded-2xl overflow-hidden`} data-testid="band-explorer">
          <div
            className="relative"
            style={{ background: "radial-gradient(ellipse at center, #17171c 0%, #000000 75%)" }}
          >
            {!ready && (
              <div className="absolute inset-0 flex items-center justify-center z-10" data-testid="band-explorer-loading">
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-500 animate-pulse">
                  Loading model&hellip;
                </span>
              </div>
            )}
            {/* @ts-ignore - model-viewer is a web component loaded via script tag in index.html */}
            <model-viewer
              ref={bandRef}
              id="band-explorer-viewer"
              data-testid="band-explorer-viewer"
              src={CLEAN_SRC}
              camera-controls
              min-camera-orbit="auto 0deg 0.05m"
              max-camera-orbit="auto 180deg 1m"
              min-field-of-view="45deg"
              max-field-of-view="45deg"
              field-of-view="45deg"
              interaction-prompt="none"
              camera-orbit="155deg 65deg 0.16m"
              camera-target="0m -0.023m 0m"
              shadow-intensity=".65"
              exposure="1"
              tone-mapping="aces"
              environment-image="neutral"
              alt="WearSpray SurfBand with a flat refill cap, translucent reservoir, and the dispensing valve"
              style={{ display: "block", width: "100%", height: "clamp(360px, 60vh, 560px)" }}
              className={showHotspots ? "" : "no-callouts"}
            >
              <button
                className="band-hotspot band-hotspot-above"
                slot="hotspot-push"
                data-position="0m 0.01599m 0m"
                data-normal="0 1 0"
                data-visibility-attribute="visible"
                data-testid="band-explorer-hotspot-push"
                aria-label="Show push to dispense"
                style={{ visibility: feature === "overview" || feature === "push" ? "visible" : "hidden" }}
                onClick={() => showFeature("push")}
              >
                <span>Push to dispense</span>
              </button>
              <button
                className="band-hotspot band-hotspot-below"
                slot="hotspot-valve"
                data-position="0m 0.00682m -0.02375m"
                data-normal="0 0 -1"
                data-visibility-attribute="visible"
                data-testid="band-explorer-hotspot-valve"
                aria-label="Show valve"
                style={{ visibility: feature === "overview" || feature === "valve" ? "visible" : "hidden" }}
                onClick={() => showFeature("valve")}
              >
                <span>Valve</span>
              </button>
              <button
                className="band-hotspot band-hotspot-below"
                slot="hotspot-cap"
                data-position="0m -0.003302m 0m"
                data-normal="0 -1 0"
                data-visibility-attribute="visible"
                data-testid="band-explorer-hotspot-cap"
                aria-label="Show refillable cap"
                style={{ visibility: feature === "overview" || feature === "cap" ? "visible" : "hidden" }}
                onClick={() => showFeature("cap")}
              >
                <span>Refillable cap</span>
              </button>
            </model-viewer>

            {feature === "exploded" && (
              <div className="absolute inset-0 z-20 bg-black" data-testid="band-explorer-exploded-overlay">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-contain"
                  style={{ height: "clamp(360px, 60vh, 560px)" }}
                  data-testid="band-explorer-exploded-video"
                >
                  <source src={EXPLODE_VIDEO} type="video/mp4" />
                </video>
                <img
                  loading="lazy"
                  src={CAD_OVERLAY}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-contain mix-blend-screen opacity-40 p-10 md:p-16 pointer-events-none"
                />
                <p className="absolute bottom-4 left-0 right-0 text-center font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500">
                  Cap &middot; valve &middot; reservoir, pulled apart
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2.5 px-5 sm:px-7 pt-6" data-testid="band-explorer-feature-nav">
            {FEATURES.map((f) => (
              <button
                key={f.key}
                type="button"
                data-testid={`band-explorer-feature-${f.key}`}
                aria-pressed={feature === f.key}
                onClick={() => showFeature(f.key)}
                className={`font-mono text-[11px] tracking-[0.12em] uppercase px-4 py-2 rounded-full border transition-colors ${
                  feature === f.key
                    ? "bg-accent text-black border-accent"
                    : "border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {feature === "exploded" ? (
            <p className="font-mono text-[10px] text-zinc-600 leading-relaxed px-5 sm:px-7 pt-4 pb-6 max-w-2xl" data-testid="band-explorer-exploded-caption">
              A rendered breakdown of the patented valve and reservoir architecture, shown for
              engineering context. Select another view to return to the live 3D model.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2.5 px-5 sm:px-7 pt-4" data-testid="band-explorer-color-picker">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-600">Color</span>
                {COLORS.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    title={c.label}
                    aria-label={`Show ${c.label} color`}
                    data-testid={`band-explorer-color-${c.key}`}
                    aria-pressed={color === c.key}
                    onClick={() => handleColor(c.key)}
                    className={`w-6 h-6 rounded-full border-2 transition-all ${
                      color === c.key ? "border-white scale-110" : "border-transparent hover:border-zinc-500"
                    }`}
                    style={{ background: c.swatch, boxShadow: color === c.key ? `0 0 0 2px #0a0a0a, 0 0 10px ${c.swatch}99` : "none" }}
                  />
                ))}
                <span className="font-mono text-[10px] tracking-[0.06em] text-zinc-500 ml-0.5">
                  {COLORS.find((c) => c.key === color)?.label}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2.5 px-5 sm:px-7 pt-4" data-testid="band-explorer-mode-toggle">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-600">Show</span>
                {MODES.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    data-testid={`band-explorer-mode-${m.key}`}
                    aria-pressed={mode === m.key}
                    onClick={() => handleMode(m.key)}
                    className={`font-mono text-[10px] tracking-[0.1em] uppercase px-3.5 py-1.5 rounded-full border transition-colors ${
                      mode === m.key
                        ? "border-accent text-accent"
                        : "border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-600"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {feature !== "exploded" && (
            <p className="font-mono text-[10px] text-zinc-600 leading-relaxed px-5 sm:px-7 pb-6 pt-4 max-w-2xl">
              Labels explain the features for this preview; they are not printed on the physical band.
              Model built from product photography.
            </p>
          )}
        </div>
      </Reveal>

      <style>{`
        model-viewer.no-callouts .band-hotspot { display: none; }
        .band-hotspot {
          width: 13px; height: 13px; padding: 0;
          border: 2px solid #0a0a0a; background: #00e5ff;
          border-radius: 50%; box-shadow: 0 0 0 3px rgba(0,229,255,0.25), 0 0 14px rgba(0,229,255,0.5);
          cursor: pointer; overflow: visible;
        }
        .band-hotspot span {
          position: absolute; white-space: nowrap; left: 50%; transform: translateX(-50%);
          padding: 7px 11px; background: #0d0d0d; border: 1px solid #27272a; border-radius: 6px;
          font: 600 11px/1 "JetBrains Mono", monospace; letter-spacing: 0.04em; color: #e4e4e7;
          text-transform: uppercase; box-shadow: 0 4px 16px rgba(0,0,0,0.5);
        }
        .band-hotspot::after { content: ''; position: absolute; width: 1px; background: #00e5ff; left: 4.5px; height: 28px; }
        .band-hotspot-above span { bottom: 40px; }
        .band-hotspot-above::after { bottom: 11px; }
        .band-hotspot-below span { top: 40px; }
        .band-hotspot-below::after { top: 11px; }
      `}</style>
    </section>
  );
}
